import type { ItemCategory } from "@/lib/types";
import type { SizeConfig } from "@/lib/listing/fieldConfigs";
import {
  getRequirementsForSubcategory,
  getWaveEnforcementStatus,
  SIZE_LINE_DERIVABLE_SPEC_KEYS,
  type ListingSpecKey,
  type SubcategorySpecRequirement,
} from "@/lib/listing/specRequirements";
import {
  getFurnitureSurfaceSpecSectionLabel,
  isFurnitureSurfaceSubcategory,
} from "@/lib/listing/furnitureSurfaceListingSpec";

export type StepTwoSizePresentation = {
  /** Wave + active + non-empty required profile */
  specEnforced: boolean;
  /** Main heading for the size/capacity control */
  fieldLabel: string;
  /** Show red asterisk instead of “(optional)” */
  showRequiredMarker: boolean;
  /** Passed through to `SizeInput` (label/placeholder/unit may be adjusted) */
  sizeInputConfig: SizeConfig;
  /**
   * When non-null, passed to `SizeInput` as `helperTextOverride` so registry-guided
   * copy replaces generic “varies” hints.
   */
  helperTextOverride: string | null;
};

/** Required keys that the size line can collect (excludes enum/boolean attrs). */
function sizeLineRequiredKeys(
  req: SubcategorySpecRequirement,
): ListingSpecKey[] {
  return req.required.filter((key) =>
    (SIZE_LINE_DERIVABLE_SPEC_KEYS as readonly string[]).includes(key),
  );
}

function singleKeyPlaceholder(
  req: SubcategorySpecRequirement,
  key: ListingSpecKey,
): string {
  const meta = req.ui[key];
  const unit = meta?.unit;
  if (meta?.inputType === "number" || meta?.inputType === "integer") {
    if (unit === "KVA") return "e.g. 5.5";
    if (unit === "HP") return "e.g. 1.5";
    if (unit === "L") return "e.g. 200";
    if (unit === "inches") return "e.g. 55";
    if (unit === "GB") {
      if (key === "ram_gb") return "e.g. 8";
      if (key === "storage_gb") return "e.g. 256";
      return "e.g. 128";
    }
    if (unit === "kg") {
      if (key === "capacity_kg") return "e.g. 12.5";
      return "e.g. 50";
    }
  }
  if (unit === "KVA") return "e.g. 5.5 KVA";
  if (unit === "HP") return "e.g. 1.5 HP";
  if (unit === "L") return "e.g. 200L";
  if (unit === "inches") return `e.g. 55" or 55 inches`;
  if (unit === "GB") {
    if (key === "ram_gb") return "e.g. 8GB RAM";
    if (key === "storage_gb") return "e.g. 256GB storage";
    return "e.g. 8GB";
  }
  if (unit === "kg") {
    if (key === "capacity_kg") return "e.g. 12.5 kg";
    return "e.g. 50 kg";
  }
  const ph = req.ui[key]?.placeholder;
  return ph && ph.trim() ? ph : "Enter the required value with units";
}

function enforcedFieldLabel(req: SubcategorySpecRequirement): string {
  const keys = sizeLineRequiredKeys(req);
  if (keys.length === 0) return "Size / specs";
  if (keys.length === 1) {
    const k = keys[0]!;
    const meta = req.ui[k];
    const u = meta?.unit;
    const base = meta?.label ?? k;
    return u ? `${base} (${u})` : base;
  }
  return keys.map((k) => req.ui[k]?.label ?? k).join(" & ");
}

function enforcedHelperText(req: SubcategorySpecRequirement): string {
  const keys = sizeLineRequiredKeys(req);
  if (keys.length === 1) {
    const k = keys[0]!;
    const numericHint = numericEnforcedHelperText(req, k);
    if (numericHint) return numericHint;

    const unit = req.ui[k]?.unit;
    if (unit === "HP") {
      return "Enter the air conditioner’s capacity in horsepower (HP), for example 1 HP or 1.5 HP.";
    }
    if (unit === "KVA") {
      return "Enter generator capacity in KVA (e.g. 5 KVA or 5.5KVA).";
    }
    if (unit === "L") {
      return "Include capacity in liters (e.g. 200L).";
    }
    if (unit === "kg" && k === "capacity_kg") {
      return "Enter cylinder capacity in kg (e.g. 6 kg or 12.5kg).";
    }
    if (unit === "inches") {
      return 'Include screen size in inches (e.g. 55 inches or 55").';
    }
    if (unit === "GB" && k === "ram_gb") {
      return "Enter RAM in GB (e.g. 8GB or 8 GB RAM).";
    }
    if (unit === "GB" && k === "storage_gb") {
      return "Enter storage in GB (e.g. 128GB or 256 GB).";
    }
    if (req.ui[k]?.inputType === "dimensions_ft") {
      return "Enter length × width in feet (height optional), e.g. 6 x 4 or 6ft x 4ft.";
    }
    if (req.ui[k]?.inputType === "enum") {
      return `Choose or type one of: ${(req.constraints[k]?.enumValues ?? []).join(", ")}.`;
    }
    return (
      req.messages[k]?.required ?? "This detail is required for this item type."
    );
  }
  return "Enter all required values in this field (include units) so we can match your listing to buyer searches.";
}

function enforcedPlaceholder(req: SubcategorySpecRequirement): string {
  const keys = sizeLineRequiredKeys(req);
  if (keys.length === 1) {
    return singleKeyPlaceholder(req, keys[0]!);
  }
  if (keys.includes("ram_gb") && keys.includes("storage_gb")) {
    return "e.g. 8GB RAM, 256GB storage";
  }
  return keys.map((k) => singleKeyPlaceholder(req, k)).join("; ");
}

function mapUnitForSizeInput(
  req: SubcategorySpecRequirement,
): SizeConfig["unit"] {
  const keys = sizeLineRequiredKeys(req);
  if (keys.length !== 1) return "varies";
  const k = keys[0]!;
  const u = req.ui[k]?.unit;
  if (u === "KVA") return "KVA";
  if (u === "HP") return "HP";
  if (u === "L") return "liters";
  if (u === "inches") return "inches";
  if (u === "kg") return "kg";
  if (u === "ft") return "ft";
  return "varies";
}

function mapValueKindForSizeInput(
  req: SubcategorySpecRequirement,
): SizeConfig["valueKind"] | undefined {
  const keys = sizeLineRequiredKeys(req);
  if (keys.length !== 1) return undefined;
  const inputType = req.ui[keys[0]!]?.inputType;
  if (inputType === "integer") return "integer";
  if (inputType === "number") return "decimal";
  return undefined;
}

function numericEnforcedHelperText(
  req: SubcategorySpecRequirement,
  key: ListingSpecKey,
): string | null {
  const meta = req.ui[key];
  if (!meta || (meta.inputType !== "number" && meta.inputType !== "integer")) {
    return null;
  }
  const unit = meta.unit;
  if (unit === "HP") {
    return "Enter horsepower as a number (e.g. 1 or 1.5). The HP unit is added for you.";
  }
  if (unit === "KVA") {
    return "Enter generator capacity as a number (e.g. 5 or 5.5). KVA is added for you.";
  }
  if (unit === "L") {
    return "Enter capacity in liters as a number (e.g. 200). L is added for you.";
  }
  if (unit === "inches") {
    return "Enter screen size as a number (e.g. 55). Inches are added for you.";
  }
  if (unit === "GB") {
    if (key === "storage_gb") {
      return "Enter storage as a whole number in GB (e.g. 128 or 256).";
    }
    if (key === "ram_gb") {
      return "Enter RAM as a whole number in GB (e.g. 8 or 16).";
    }
    return "Enter a whole number in GB.";
  }
  if (unit === "kg") {
    if (key === "capacity_kg") {
      return "Enter cylinder capacity as a number in kg (e.g. 6 or 12.5). kg is added for you.";
    }
    return "Enter load capacity as a number in kg.";
  }
  return "Enter a number only — units are added automatically.";
}

/**
 * Step 2 “size/capacity” line: when spec wave enforcement applies, labels and hints
 * follow the subcategory registry; otherwise category `fieldConfigs` stay as-is.
 */
export function getStepTwoSizePresentation(params: {
  category: ItemCategory;
  subcategoryId: string;
  activeSubcategoryIds: Iterable<string>;
  fallback: SizeConfig;
}): StepTwoSizePresentation {
  const wave = getWaveEnforcementStatus(
    params.subcategoryId,
    params.activeSubcategoryIds,
  );
  const req = getRequirementsForSubcategory(params.subcategoryId);
  const hasRequiredProfile = Boolean(
    req && sizeLineRequiredKeys(req).length > 0,
  );

  if (!wave.shouldEnforce || !hasRequiredProfile || !req) {
    return {
      specEnforced: false,
      fieldLabel: params.fallback.label || "Size",
      showRequiredMarker: false,
      sizeInputConfig: params.fallback,
      helperTextOverride: null,
    };
  }

  const label = enforcedFieldLabel(req);
  const placeholder = enforcedPlaceholder(req);
  const helper = enforcedHelperText(req);

  if (params.fallback.inputType === "text") {
    const valueKind = mapValueKindForSizeInput(req);
    return {
      specEnforced: true,
      fieldLabel: label,
      showRequiredMarker: true,
      sizeInputConfig: {
        ...params.fallback,
        label,
        placeholder,
        unit: mapUnitForSizeInput(req),
        inputType: "text",
        valueKind,
      },
      helperTextOverride: helper,
    };
  }

  if (params.fallback.inputType === "select") {
    return {
      specEnforced: true,
      fieldLabel: label,
      showRequiredMarker: true,
      sizeInputConfig: {
        ...params.fallback,
        label,
      },
      helperTextOverride: helper,
    };
  }

  if (params.fallback.inputType === "dimensions") {
    return {
      specEnforced: true,
      fieldLabel: label,
      showRequiredMarker: true,
      sizeInputConfig: {
        ...params.fallback,
        label,
      },
      helperTextOverride: helper,
    };
  }

  return {
    specEnforced: true,
    fieldLabel: label,
    showRequiredMarker: true,
    sizeInputConfig: { ...params.fallback, label, placeholder },
    helperTextOverride: helper,
  };
}

/** Step 2 section heading for furniture surface panels (Phase 3). */
export function getFurnitureSurfaceStepTwoSectionLabel(
  subcategoryId: string | null | undefined,
): string | null {
  if (!isFurnitureSurfaceSubcategory(subcategoryId)) return null;
  return getFurnitureSurfaceSpecSectionLabel(subcategoryId);
}
